import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useClient } from '@/context/ClientContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Plus, Search, ExternalLink, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

function statusMeta(c) {
  if (c.intake_status === 'pending') return { label: 'Awaiting intake', tone: 'warning' };
  if (c.aeo_status === 'running') return { label: 'AEO running…', tone: 'info' };
  if (c.aeo_status === 'failed') return { label: 'AEO failed', tone: 'destructive' };
  return { label: 'Active', tone: 'success' };
}

const TONE_CLASS = {
  warning: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30',
  info: 'bg-primary/10 text-primary border-primary/30',
  success: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30',
  destructive: 'bg-destructive/10 text-destructive border-destructive/30',
};

export default function Clients() {
  const navigate = useNavigate();
  const { clients, refresh, selectClient } = useClient();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [stub, setStub] = useState({ company_name: '', website: '', contact_email: '', contact_first_name: '', contact_last_name: '' });
  const [creating, setCreating] = useState(false);

  useEffect(() => { refresh(); }, []); // eslint-disable-line

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return clients;
    return clients.filter(c =>
      (c.company_name || '').toLowerCase().includes(needle) ||
      (c.industry || '').toLowerCase().includes(needle) ||
      (c.website || '').toLowerCase().includes(needle)
    );
  }, [clients, q]);

  const createStub = async () => {
    if (!stub.company_name.trim()) return toast.error('Company name required');
    setCreating(true);
    try {
      const { data } = await api.post('/clients/stub', stub);
      await refresh();
      setOpen(false);
      setStub({ company_name: '', website: '', contact_email: '', contact_first_name: '', contact_last_name: '' });
      if (stub.contact_email) {
        toast.success(`Created ${data.client.company_name} + invited ${stub.contact_email}. They can sign in with Google now.`);
      } else {
        toast.success(`Created ${data.client.company_name}. Send them an invite from Team or complete intake yourself.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create client');
    } finally {
      setCreating(false);
    }
  };

  const openClient = (clientId) => {
    selectClient(clientId);
    navigate('/dashboard');
  };

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Client portfolio</div>
          <h1 className="mt-2 font-display text-3xl tracking-tight">All clients</h1>
          <p className="mt-2 text-sm text-muted-foreground">View, sort and drill into every client your team is engineering authority for.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="clients-search"
              className="pl-9 w-[260px]"
              placeholder="Search clients…"
              value={q}
              onChange={e => setQ(e.target.value)}
            />
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="clients-new-stub-btn"><Plus className="h-4 w-4 mr-1" /> New client + invite</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Quick add client</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-xs text-muted-foreground">
                  Create a client portal in one step. Optionally invite the client contact — they'll sign in with Google
                  using the email below and complete the intake themselves to kick off the AEO process.
                </p>
                <Field label="Company name *">
                  <Input data-testid="stub-company-name" value={stub.company_name} onChange={e => setStub({ ...stub, company_name: e.target.value })} placeholder="Acme, Inc." />
                </Field>
                <Field label="Website">
                  <Input data-testid="stub-website" value={stub.website} onChange={e => setStub({ ...stub, website: e.target.value })} placeholder="https://acme.com" />
                </Field>
                <div className="border-t border-border pt-4">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Optional — invite client contact</div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="First name">
                      <Input value={stub.contact_first_name} onChange={e => setStub({ ...stub, contact_first_name: e.target.value })} />
                    </Field>
                    <Field label="Last name">
                      <Input value={stub.contact_last_name} onChange={e => setStub({ ...stub, contact_last_name: e.target.value })} />
                    </Field>
                  </div>
                  <Field label="Email (must match their Google login)">
                    <Input data-testid="stub-contact-email" type="email" value={stub.contact_email} onChange={e => setStub({ ...stub, contact_email: e.target.value })} placeholder="contact@acme.com" />
                  </Field>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button data-testid="stub-create-btn" onClick={createStub} disabled={creating}>
                  {creating ? 'Creating…' : 'Create + invite'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button variant="outline" onClick={() => navigate('/onboarding')}>
            <Sparkles className="h-4 w-4 mr-1" /> Full onboarding
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.length === 0 && (
          <div className="col-span-full rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
            {clients.length === 0 ? 'No clients yet. Create your first one with "New client + invite".' : 'No clients match your search.'}
          </div>
        )}
        {filtered.map(c => {
          const s = statusMeta(c);
          return (
            <div
              key={c.client_id}
              data-testid={`client-card-${c.client_id}`}
              className="card-hover rounded-xl border border-border bg-card p-6 cursor-pointer"
              onClick={() => openClient(c.client_id)}
              role="button"
              tabIndex={0}
              onKeyDown={e => (e.key === 'Enter' ? openClient(c.client_id) : null)}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-display text-lg truncate">{c.company_name}</div>
                  <div className="mt-1 text-xs text-muted-foreground truncate">{c.industry || '—'} {c.website ? `· ${c.website.replace(/^https?:\/\//, '')}` : ''}</div>
                </div>
                <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${TONE_CLASS[s.tone]}`}>{s.label}</Badge>
              </div>
              <div className="mt-6 flex items-end gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Authority Score™</div>
                  <div className="score-num text-4xl text-primary">{c.authority_score ?? '—'}</div>
                </div>
                <div className="pb-1">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Grade</div>
                  <div className="font-display text-lg">{c.letter_grade || '—'}</div>
                </div>
                <div className="pb-1 ml-auto">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Tier</div>
                  <div className="text-xs font-medium">{c.certification || '—'}</div>
                </div>
              </div>
              <div className="mt-6 flex items-center justify-between border-t border-border pt-3 text-xs text-muted-foreground">
                <span>{c.intake_status === 'pending' ? 'Intake pending' : `Updated ${timeAgo(c.updated_at)}`}</span>
                <span className="inline-flex items-center gap-1 text-primary">Open <ExternalLink className="h-3 w-3" /></span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
